import type { BiomassDataRow } from './productionUnitTable'
import { getZonedIsoDate, resolveTimeZone } from './timeBucketing'

/**
 * Per-row "same-day siblings" summary. Built once per render so the Database
 * table can surface a transparency badge on rows that share `(lot, calendar
 * date)` with one or more other rows.
 *
 * Lives in `src/utils/` so both Database (Datatable) and Biomass screens can
 * reuse it without introducing a cross-screen dependency on either component.
 *
 * v2.1 finding #4: until v2.2 redesigns Sampling write semantics (upsert vs
 * insert), the row redundancy is preserved on disk. This helper powers the
 * visual mitigation: each row is annotated with the total count of rows it
 * shares the day with so the UI can render `({count} rows this day)` and a
 * drill-down listing every sibling.
 *
 * v2.1 finding #47 follow-up: the day-key derivation now accepts an optional
 * `timeZone` so callers can bucket by the farm-local calendar day instead of
 * the UTC prefix. Without it, late-evening rows in negative-UTC farms whose
 * isoZ has already crossed into the next UTC day would collide with the next
 * morning's rows on the same lot — the same asymmetry that affected the
 * `(lot, day)` dedup detectors before #47 was wired through. When omitted,
 * the helper preserves the legacy UTC-prefix bucketing for backward
 * compatibility with callers that don't yet thread the farm TZ.
 */
export type SameDayRowsSummary = {
  /** Total number of rows in the (lot, day) bucket, including this row. */
  count: number
  /** Row IDs of every row in the bucket, in input order. */
  rowIds: string[]
}

export type SameDayRowsOptions = {
  /** IANA timezone (e.g. `America/Argentina/Buenos_Aires`). When provided,
   *  the day key is derived from the farm-local calendar day instead of the
   *  UTC prefix. Falls back to `resolveTimeZone(...)` (browser TZ) for empty
   *  / unset values. */
  timeZone?: string
}

const toCalendarDayKey = (value: unknown, options?: SameDayRowsOptions): string => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (options?.timeZone) {
    const zoned = getZonedIsoDate(trimmed, resolveTimeZone(options.timeZone))
    if (zoned) return zoned
  }
  const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed)
  if (isoMatch) return isoMatch[1]
  const parsed = Date.parse(trimmed)
  if (!Number.isFinite(parsed)) return ''
  const d = new Date(parsed)
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const resolveGroupKey = (row: BiomassDataRow, options?: SameDayRowsOptions): string => {
  const dayKey = toCalendarDayKey(row.date, options)
  if (!dayKey) return ''
  const lotId = String(row.lotId ?? '').trim()
  if (lotId) return `lot-id:${lotId}::${dayKey}`
  const lotKey = String(row.lot ?? '').trim()
  if (lotKey) return `lot-key:${lotKey.toLowerCase()}::${dayKey}`
  return ''
}

/**
 * Group `rows` by `(lot, calendar day)` and return a map from each row ID to a
 * summary of how many other rows share the same bucket.
 *
 * Rows without a usable lot or date are not bucketed and are absent from the
 * returned map (treat absence as "no siblings known").
 *
 * Pure and side-effect free.
 */
export const computeSameDayRowsByRowId = (
  rows: ReadonlyArray<BiomassDataRow>,
  options?: SameDayRowsOptions
): Map<string, SameDayRowsSummary> => {
  const result = new Map<string, SameDayRowsSummary>()
  if (!Array.isArray(rows) || rows.length === 0) return result

  const grouped = new Map<string, string[]>()
  for (const row of rows) {
    if (!row || typeof row.id !== 'string' || !row.id) continue
    const key = resolveGroupKey(row, options)
    if (!key) continue
    const bucket = grouped.get(key)
    if (bucket) {
      bucket.push(row.id)
    } else {
      grouped.set(key, [row.id])
    }
  }

  grouped.forEach((rowIds) => {
    const summary: SameDayRowsSummary = { count: rowIds.length, rowIds }
    for (const id of rowIds) {
      result.set(id, summary)
    }
  })

  return result
}

/**
 * Return every row that shares `(lot, calendar day)` with the given row,
 * sorted by date ascending. Used by the drill-down popover.
 *
 * Pure and side-effect free.
 */
export const findSameDayRowsForRow = (
  targetRow: Pick<BiomassDataRow, 'id' | 'date' | 'lot' | 'lotId'>,
  rows: ReadonlyArray<BiomassDataRow>,
  options?: SameDayRowsOptions
): BiomassDataRow[] => {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const targetKey = resolveGroupKey(targetRow as BiomassDataRow, options)
  if (!targetKey) return []
  const matched = rows.filter((row) => resolveGroupKey(row, options) === targetKey)
  return [...matched].sort((left, right) => {
    const leftTime = Date.parse(String(left.date ?? ''))
    const rightTime = Date.parse(String(right.date ?? ''))
    const leftValid = Number.isFinite(leftTime)
    const rightValid = Number.isFinite(rightTime)
    if (leftValid && rightValid && leftTime !== rightTime) return leftTime - rightTime
    if (leftValid && !rightValid) return -1
    if (!leftValid && rightValid) return 1
    return String(left.id ?? '').localeCompare(String(right.id ?? ''))
  })
}
